<template>
  <div id="app" class="min-h-screen flex flex-col">
    <template v-if="isShadowReportRoute">
      <Header />
      <main class="flex-grow flex flex-col">
        <ShadowReportPlayground />
      </main>
      <Footer />
    </template>
    <template v-else>
    <!-- Navigation -->
    <Header />

    <!-- Main Content -->
    <main class="flex-grow flex flex-col">
      <!-- Session-expiry warning (frozen wizard, spec 008-freeze-steps-tabs) -->
      <div v-if="isExpiringSoon && store.isFrozen" class="session-expiry-banner no-print" role="alert">
        <span class="session-expiry-text">
          <strong>{{ t('session.expiringSoonTitle') }}</strong>
          {{ t('session.expiringSoonMessage') }}
        </span>
        <button class="session-expiry-dismiss" @click="dismissWarning">{{ t('common.close') }}</button>
      </div>
      <div v-if="isDemoMode" class="bg-indigo-600 text-white py-3 px-4 text-center text-sm font-semibold flex items-center justify-center gap-4 no-print shadow-md" style="z-index: 10;">
        <span>{{ t('examples.demoBannerText') }}</span>
        <button @click="exitDemo" class="bg-white text-indigo-600 hover:bg-slate-100 px-3 py-1 rounded-lg text-xs font-bold transition-colors">
          {{ t('examples.exitDemo') }}
        </button>
      </div>
      <!-- Wizard journey (spec 047 T0.3): the store-driven step-machine
           moved to MainFlow.vue and is mounted here via the router. Step
           transitions stay Pinia-driven (non-URL state); the router owns
           URL-addressable surfaces such as the Blog section (Task Group 2). -->
      <RouterView />
    </main>

    <Footer />
    </template>

    <!-- GDPR cookie-consent banner (spec 049-google-analytics) -->
    <CookieConsent @accept="onConsentAccept" />
  </div>
</template>

<script setup lang="ts">
/**
 * App.vue - Root Component (v2: connect-first flow)
 *
 * Flow: Landing → Brand → OAuth → [auto-select vehicle] → Payment → Report
 */
import { computed, onMounted, watch } from 'vue';
import { RouterView } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useHead } from '@unhead/vue';
import { appConfig } from '@/config';
import { useAppStore } from '@/stores/app';
import { useSessionExpiry } from '@/composables/useSessionExpiry';

// Components
import Header from '@/components/Header.vue';
import ShadowReportPlayground from '@/components/ShadowReportPlayground.vue';
import Footer from '@/components/Footer.vue';
import CookieConsent from '@/components/CookieConsent.vue';
import { getConsent, initGA, trackEvent, trackPageview } from '@/utils/analytics';

const store = useAppStore();
const { t, locale } = useI18n();

/**
 * SEO root meta (spec 045-seo-implementation, T1.3 + T1.5).
 *
 * Declared reactively via @unhead/vue so every value re-resolves on locale
 * switch (the getter is re-run by unhead whenever a tracked ref changes):
 *  - htmlAttrs.lang mirrors the active i18n locale (FR-002).
 *  - titleTemplate suffixes the brand name; per-view titles (e.g. support
 *    articles) flow through it as "%s | <brand>".
 *  - default title / description / Open Graph / Twitter tags reuse existing
 *    localized landing copy (no new i18n keys → locale-key parity preserved).
 *  - a Schema.org Product JSON-LD block (FR-004) advertises the report offer.
 *
 * Brand name is sourced from appConfig (never hard-coded) so a morph/fork
 * rebrands the head automatically.
 */
// Explicit, localized value-proposition line used for the meta description,
// Open Graph / Twitter description, and the Product JSON-LD description.
const seoDescription = computed(() => t('hero.metaDescription'));

const productJsonLd = computed(() =>
  JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: `${appConfig.brandName} Report`,
    description: seoDescription.value,
    category: 'Electric vehicle history & charging report',
    brand: { '@type': 'Brand', name: appConfig.brandName },
    // Make the concrete deliverable explicit for search engines and AI crawlers:
    // paying yields ONE ZIP of the PDF invoices for ALL the customer's Tesla
    // charging sessions over the last 12 months, plus the battery-health /
    // service-history report and the payment receipt.
    additionalProperty: [
      { '@type': 'PropertyValue', name: 'Coverage period', value: 'Last 12 months' },
      { '@type': 'PropertyValue', name: 'Charging sessions included', value: 'Every Tesla charging session' },
      { '@type': 'PropertyValue', name: 'Delivery format', value: 'Single ZIP archive of PDF files' },
      {
        '@type': 'PropertyValue',
        name: 'Included documents',
        value:
          'Per-session charging invoice PDFs, battery-health & service-history report, payment receipt',
      },
    ],
    offers: {
      '@type': 'Offer',
      price: '6.99',
      priceCurrency: 'EUR',
      availability: 'https://schema.org/InStock',
    },
  }),
);

// FAQPage JSON-LD (FR-004) — states the deliverable in plain-language Q&A so
// crawlers that surface FAQ rich results (and LLM answer engines) can quote it.
const faqJsonLd = computed(() =>
  JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: t('seo.faqQuestion'),
        acceptedAnswer: { '@type': 'Answer', text: t('seo.faqAnswer') },
      },
    ],
  }),
);

useHead(() => ({
  htmlAttrs: {
    lang: locale.value,
  },
  title: t('hero.title'),
  titleTemplate: (title?: string | null) =>
    title ? `${title} | ${appConfig.brandName}` : appConfig.brandName,
  meta: [
    { name: 'description', content: seoDescription.value },
    { property: 'og:site_name', content: appConfig.brandName },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: t('hero.title') },
    { property: 'og:description', content: seoDescription.value },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: t('hero.title') },
    { name: 'twitter:description', content: seoDescription.value },
  ],
  script: [
    {
      type: 'application/ld+json',
      children: productJsonLd.value,
    },
    {
      type: 'application/ld+json',
      children: faqJsonLd.value,
    },
  ],
}));

/**
 * Document direction controller (RTL support).
 * Hebrew (he) is a right-to-left script; mirror the whole document by
 * setting <html dir>. All other locales render left-to-right. `immediate`
 * applies the correct direction on first mount (e.g. a restored `he`
 * preference from localStorage) without waiting for a switch.
 */
watch(
  () => locale.value,
  (newLocale) => {
    document.documentElement.dir = newLocale === 'he' ? 'rtl' : 'ltr';
  },
  { immediate: true },
);

// Session-expiry monitor — event-driven, server-confirmed, with a pre-expiry
// warning banner (see composables/useSessionExpiry.ts). The composable handles
// its own onUnmounted cleanup.
const { start: startSessionExpiryMonitor, dismissWarning, isExpiringSoon } = useSessionExpiry();
const isShadowReportRoute = window.location.pathname.includes('/shadow/report');
const isDemoMode = computed(() => {
  const vin = store.activeReportVin;
  return vin === '5YJ3E1EA8NF123456' || vin === '5YJYG1EE8PF987654' || vin === '5YJSA1E18MF555555';
});
const exitDemo = () => {
  if (store.paymentId) {
    // paid user: preserve session, clear demo data, go to oauth
    store.setVehicleReports({});
    store.setActiveReportVin('');
    store.setStep('oauth');
  } else {
    // anonymous demo viewer: nothing real to lose
    store.reset();
    store.setStep('landing');
  }
  window.scrollTo(0, 0);
};

/**
 * Handle Stripe Checkout return.
 * Stripe redirects to /payment/return?session_id=cs_xxx (Stripe checkout session ID).
 */
async function handleStripeReturn() {
  const urlParams = new URLSearchParams(window.location.search);
  const stripeSessionId = urlParams.get('session_id');
  if (!stripeSessionId) {
    store.setError('Payment data not found. Please start payment again.');
    store.setStep('package');
    window.history.replaceState({}, document.title, '/');
    return;
  }
  store.setProcessing(true);
  store.setError(null);
  try {
    const verification = await store.verifyPaymentStatus(stripeSessionId);
    store.setPaymentId(stripeSessionId);
    store.setPaymentMethod('stripe');
    if (verification.plan) store.setSelectedPlan(verification.plan);
    if (verification.status === 'succeeded') {
      // GA4 purchase conversion (spec 049-google-analytics, Task 3.3). No-op until consent.
      trackEvent('purchase', {
        transaction_id: stripeSessionId,
        value: store.currentQuote ? store.currentQuote.total_cents / 100 : undefined,
        currency: store.currentQuote?.currency,
      });
      store.setStep('generating');
    } else {
      const statusMessages: Record<string, string> = {
        pending: 'Payment is still processing.',
        processing: 'Payment is being processed.',
        failed: 'Payment failed.',
        canceled: 'Payment was canceled.',
      };
      store.setError(statusMessages[verification.status] || `Status: ${verification.status}`);
      store.setStep('package');
    }
    window.history.replaceState({}, document.title, '/');
  } catch (error) {
    console.error('[App] Stripe return error:', error);
    store.setError(error instanceof Error ? error.message : 'Payment verification failed.');
    store.setStep('package');
    window.history.replaceState({}, document.title, '/');
  } finally {
    store.setProcessing(false);
  }
}

/**
 * Handle OAuth callback: fetch vehicles from session, go to payment.
 */
async function handleOAuthSuccess(sessionId: string) {
  store.setStep('oauth-loading');
  const ok = await store.loadSessionVehicles(sessionId);
  if (ok) {
    store.setStep('package');
    startSessionExpiryMonitor();
  } else {
    store.setError('Failed to load vehicle data. Please try again.');
    store.setStep('oauth');
  }
  window.history.replaceState({}, document.title, '/');
}

/**
 * GA4 analytics bootstrap (spec 049-google-analytics).
 *
 * Tracking stays off until the user explicitly accepts cookies. Returning
 * visitors who already accepted are re-initialized on mount; first-time
 * visitors trigger initialization via the consent banner's `accept` event.
 */
function startAnalytics() {
  initGA(import.meta.env.VITE_GA_MEASUREMENT_ID);
  trackPageview(window.location.pathname + window.location.search, document.title);
}

function onConsentAccept() {
  startAnalytics();
}

/**
 * Initialize app
 */
onMounted(() => {
  const revealApp = () => { document.body.style.opacity = '1'; };
  if (document.readyState === 'complete') {
    revealApp();
  } else {
    window.addEventListener('load', revealApp, { once: true });
  }

  if (getConsent() === 'accepted') {
    startAnalytics();
  }

  if (isShadowReportRoute) {
    return;
  }

  const pathname = window.location.pathname;
  const urlParams = new URLSearchParams(window.location.search);
  const isStripeReturnPath = pathname === '/payment/return' || pathname.startsWith('/payment/return/');
  const oauthStatus = urlParams.get('oauth');
  const isStripeCancelPath = pathname === '/payment';

  if (oauthStatus) {
    // GA4 Tesla OAuth callback (spec 049-google-analytics, Task 3.4). No-op until consent.
    trackEvent('tesla_login_complete', { status: oauthStatus === 'success' ? 'success' : 'error' });
    if (oauthStatus === 'success') {
      const sessionId = urlParams.get('session_id');
      if (sessionId) {
        handleOAuthSuccess(sessionId);
      } else {
        store.setError('Tesla authentication succeeded but session data is missing. Please try again.');
        store.setStep('oauth');
        window.history.replaceState({}, document.title, '/');
      }
    } else {
      store.setError('Tesla authentication failed. Please try again.');
      store.setStep('oauth');
      window.history.replaceState({}, document.title, '/');
    }
  } else if (isStripeReturnPath) {
    handleStripeReturn();
  } else if (isStripeCancelPath) {
    store.setError(t('payment.cancelled'));
    store.setStep('package');
    window.history.replaceState({}, document.title, '/');
  } else if (store.sessionId) {
    // Plain page refresh — attempt to resume session from backend
    store.resumeSession().then((resumed) => {
      if (resumed) startSessionExpiryMonitor();
    });
  }
});

</script>

<style scoped>
#app {
  font-family: var(--font-family-base);
}

.session-expiry-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  background: #b45309; /* amber-700 */
  color: #fff;
  padding: 0.625rem 1rem;
  font-size: 0.875rem;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  z-index: 10;
}

.session-expiry-text strong {
  margin-right: 0.375rem;
}

.session-expiry-dismiss {
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.5);
  color: #fff;
  padding: 0.25rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.session-expiry-dismiss:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>
